import type { UserTier } from '../types';

/**
 * Endowed-progress-beregning for tier-fremdriftsbaren (UX-prinsipp #2 Goal Gradient,
 * grep C). Bygger på den ferdige tier-motoren (USER_TIERS / contributionScore) —
 * se services/visitService.ts og docs/plan-ux-principles-retention.md.
 *
 * To atferdstrekk:
 *  1. «Endowed progress» (Nunes & Drèze): baren måler KUMULATIV poengsum mot neste
 *     tier — ikke unike plasser alene — så verifiserte bidrag også teller, og baren
 *     starter aldri visuelt på tom (floorPct). Folk fullfører noe de føler er påbegynt.
 *  2. Nær-mål-akselerasjon: når man er 1–2 unna neste tier flagges det for ekstra
 *     visuell emphasis («Bare 1 igjen!»).
 *
 * Regnes rent ut fra spotsToNextTier + nextTier.threshold (score = threshold − rest),
 * så ingen ny state trengs der baren allerede har disse feltene.
 */

/** Minste synlige fyll i prosent — baren skal aldri se helt tom ut. */
export const TIER_BAR_FLOOR_PCT = 6;

/** Terskel (antall igjen) for nær-mål-emphasis. */
export const NEAR_TIER_GOAL_REMAINING = 2;

/**
 * Kumulativ fremdrift mot neste tier i prosent, aldri under gulvet og aldri over 100.
 * Ved toppnivå (nextTier === null) er man ferdig → 100.
 */
export function computeTierBarPct(
  spotsToNextTier: number,
  nextTier: UserTier | null,
  floorPct: number = TIER_BAR_FLOOR_PCT,
): number {
  if (!nextTier || nextTier.threshold <= 0) return 100;
  const remaining = Math.max(0, spotsToNextTier);
  const pct = (1 - remaining / nextTier.threshold) * 100;
  return Math.min(100, Math.max(floorPct, pct));
}

/**
 * Er brukeren nær nok neste tier til å fortjene ekstra emphasis? Kun når det faktisk
 * gjenstår noe (> 0) og det er innenfor terskelen. Ved toppnivå: alltid false.
 */
export function isNearTierGoal(
  spotsToNextTier: number,
  nextTier: UserTier | null,
): boolean {
  return !!nextTier && spotsToNextTier > 0 && spotsToNextTier <= NEAR_TIER_GOAL_REMAINING;
}
